import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      checked = false,
      indeterminate = false,
      onChange,
      disabled,
      children,
      className,
      id,
    },
    ref,
  ) => {
    const resolvedChecked = indeterminate ? 'indeterminate' : checked;

    return (
      <label
        className={cn(
          'inline-flex cursor-pointer items-start gap-2 text-sm text-[var(--inv-text)]',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        htmlFor={id}>
        <CheckboxPrimitive.Root
          ref={ref}
          id={id}
          disabled={disabled}
          checked={resolvedChecked}
          onCheckedChange={val => onChange?.(val === true)}
          className={cn(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid border-[var(--inv-border-strong,#d4c4b0)] bg-[var(--inv-surface)] transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]',
            'data-[state=checked]:border-[var(--inv-accent)] data-[state=checked]:bg-[var(--inv-accent)] data-[state=checked]:text-[var(--inv-on-accent)]',
            'data-[state=indeterminate]:border-[var(--inv-accent)] data-[state=indeterminate]:bg-[var(--inv-accent)] data-[state=indeterminate]:text-[var(--inv-on-accent)]',
          )}>
          <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
            {indeterminate ? (
              <Minus className="h-3 w-3" strokeWidth={3} />
            ) : (
              <Check className="h-3 w-3" strokeWidth={3} />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {children ? <span className="min-w-0 flex-1">{children}</span> : null}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
